import { Button } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { Formik, FormikErrors } from "formik";
import * as Yup from "yup";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import GeneralValidationError from "@/common/components/Error/GeneralValidationError";
import FormActions from "@/common/components/Form/FormActions";
import AppIcon from "@/common/components/Icons/AppIcon";
import { GeoHelper } from "@/common/helpers/geo";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import {
  GeneralGeolocationDto,
  GeneralGeolocationInputDto,
  GeneralGeolocationOfEntityUpdateDto,
  GeneralGeolocationOfEntityUpdateResultDto,
} from "@/core/api/generated/v1";

import GeneralGeolocationInput from "./GeneralGeolocationInput";

export interface GeneralGeolocationOfEntityUpdateProps {
  initialValue: Nil<GeneralGeolocationDto>;
  saveFunc: (
    newValue: GeneralGeolocationOfEntityUpdateDto | undefined,
  ) => Promise<GeneralGeolocationOfEntityUpdateResultDto>;
  onSaved: (newValue: GeneralGeolocationOfEntityUpdateResultDto) => void;
}

export default function GeneralGeolocationOfEntityUpdate({
  initialValue,
  saveFunc,
  onSaved,
}: GeneralGeolocationOfEntityUpdateProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();

  return (
    <Formik<GeneralGeolocationOfEntityUpdateDto & BaseFormikValues>
      enableReinitialize={false}
      initialValues={{
        geolocation: initialValue ? { ...initialValue } : undefined,
        submit: "",
      }}
      validationSchema={Yup.object().shape({
        // name: Yup.string().required("Name is required"),
      })}
      onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
        try {
          const result = await saveFunc({
            ...values,
            geolocation:
              GeoHelper.prepareGeneralGeolocationInputDtoForApi(values.geolocation) || undefined,
          });
          enqueueSnackbar("Geolocation saved.", { variant: "success" });
          onSaved(result);

          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err: any) {
          if (mounted.current) {
            ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }
      }}
    >
      {({ errors, handleSubmit, isSubmitting, values, setFieldValue }) => {
        return (
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Box>
                {!values.geolocation && (
                  <Box>
                    <NoDataAlert fullWidth={false} />

                    <Stack
                      direction='row'
                      sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: { lg: "flex-end" },
                      }}
                    >
                      <Button
                        variant='text'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='add' />}
                        onClick={() => {
                          setFieldValue("geolocation", {
                            ...initialValue,
                          });
                        }}
                      >
                        Add geolocation
                      </Button>
                    </Stack>
                  </Box>
                )}

                {values.geolocation && (
                  <Stack spacing={1}>
                    <GeneralGeolocationInput
                      value={values.geolocation}
                      errors={errors?.geolocation as FormikErrors<GeneralGeolocationInputDto>}
                      onChange={(newValue) => {
                        setFieldValue("geolocation", newValue);
                      }}
                    />

                    <Stack
                      direction='row'
                      sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: { lg: "flex-end" },
                      }}
                    >
                      <Button
                        variant='text'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='remove' />}
                        onClick={() => {
                          setFieldValue("geolocation", undefined);
                        }}
                      >
                        Remove geolocation
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Box>

              <GeneralValidationError sx={{ my: 1 }} errors={errors} />

              <FormActions>
                <Button
                  color='primary'
                  loading={isSubmitting}
                  fullWidth
                  type='submit'
                  variant='contained'
                >
                  Save
                </Button>
              </FormActions>
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
}
